import React, { useMemo } from 'react';
import { Box, Typography, Link } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../../../../hooks/storeHooks';
import { useToast } from '../../../../../context/ToasterContext';
import { fetchPdfMedia, getEventStatusType } from '../../../../../helpers/common';
import { DEFAULT_DATE_FORMAT, fDate } from '../../../../../utils/formatTime';
import { ROUTE_PATHS } from '../../../../../routes';
import { CorporateEventStatusEnum, EventTypeFilterEnum } from '../../../../../constants/enums';
import { MdKeyboardArrowRight } from 'react-icons/md';
import RecentQuoteCard from './RecentQuoteCard/RecentQuoteCard';
import { EventStatusEnum } from '../../../../../types/eventTypes';
import './RecentQuotes.scss';
import { getFullName } from '../../../../../utils/commonUtils';

interface RecentQuotesProps {
  eventType: EventTypeFilterEnum;
  eventId: string;
  eventStatus: EventStatusEnum | CorporateEventStatusEnum;
}

const RecentQuotes: React.FC<RecentQuotesProps> = ({ eventType, eventId, eventStatus }) => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { userDetails } = useAppSelector((state) => state.user);

  // Get bids from Redux based on event type
  const b2cBids = useAppSelector((state) => state.events.bids);
  const b2bBids = useAppSelector((state) => state.corporateEvent.corporateEventBids);

  const quote = useMemo(() => {
    if (eventType === EventTypeFilterEnum.B2B) {
      if (!b2bBids?.currentBid?.id) return null;
      const quoteAmount = b2bBids.currentBid.amount + b2bBids.currentBid.vat + b2bBids.currentBid.municipalityTax;

      return {
        id: b2bBids.currentBid.id,
        quoteAmount: quoteAmount,
        quoteDate: b2bBids.currentBid.createdAt ? fDate(b2bBids.currentBid.createdAt, DEFAULT_DATE_FORMAT) : undefined,
        brandedQuotePdfUrl: b2bBids.currentBid.brandedQuotePdfUrl || b2bBids.currentBid.quoteUrl || '',
      };
    } else if (eventType === EventTypeFilterEnum.Prepaid) {
      // For Prepaid, return empty array
      return null;
    } else {
      if (!b2cBids?.currentBid?.id) return null;
      const quoteAmount = b2cBids.currentBid.amount + b2cBids.currentBid.vat + b2cBids.currentBid.municipalityTax;

      return {
        id: b2cBids.currentBid.id,
        quoteAmount: quoteAmount,
        quoteDate: b2cBids.currentBid.createdAt ? fDate(b2cBids.currentBid.createdAt, DEFAULT_DATE_FORMAT) : undefined,
        brandedQuotePdfUrl: b2cBids.currentBid.brandedQuotePdfUrl || b2cBids.currentBid.quoteUrl || '',
      };
    }
  }, [eventType, b2cBids, b2bBids]);

  const handleViewQuotePDF = async (url: string) => {
    if (!url) {
      showToast('Quote PDF not available', () => { }, 'warning');
      return;
    }
    const isOpened = await fetchPdfMedia(url);
    if (!isOpened) {
      showToast('Allow browser popups to view media', () => { }, 'warning');
    }
  };

  const handleViewAll = () => {
    const type = getEventStatusType(eventStatus, eventType);
    if (eventType === EventTypeFilterEnum.B2B) {
      navigate(`${ROUTE_PATHS.B2B.EVENT_DETAIL.replace(':id', eventId)}?tab=quotes&type=${type}`);
    } else {
      navigate(`${ROUTE_PATHS.EVENT_DETAIL.replace(':id', eventId)}?tab=my_quote&type=${type}`);
    }
  };

  if (!quote || !quote?.id) {
    return null;
  }

  return (
    <Box className="recentQuotes">
      <Box className="recentQuotesHeader">
        <Typography className="recentQuotesTitle">Latest Quote</Typography>
        <Link component="button" variant="body2" onClick={handleViewAll} className="recentQuotesViewAll">
          View all
          <MdKeyboardArrowRight />
        </Link>
      </Box>
      <Box className="recentQuotesList">
        <RecentQuoteCard
          vendorName={getFullName(userDetails?.firstName || '', userDetails?.lastName || '')}
          quoteAmount={quote.quoteAmount}
          quoteDate={quote.quoteDate}
          onViewPDF={quote.brandedQuotePdfUrl ? () => handleViewQuotePDF(quote.brandedQuotePdfUrl) : undefined}
        />
      </Box>
    </Box>
  );
};

export default RecentQuotes;
